import { useFormikContext } from "formik";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import walletReceive from "../../../assets/cardReceive.svg";
import blackCart from "../../../assets/cardReceiveBlack.svg";

import walletSend from "../../../assets/cardSend.svg";
import {
  ProposalAction,
  ProposalBuyOption,
} from "../../../common/enums/proposals.enums";
import { IDetailNftData } from "../../../common/interfaces/common.interface";
import {
  ICreateProposalForm,
  ICreateProposalProps,
  ProposalTypeNew,
} from "../../../common/interfaces/proposals.interface";
import WizardField from "../../WizardField/WizardField";
import "./CreateProposal.scss";
import Fees from "../../Fees/Fees";
import {
  getMintMetadata,
  parseMetadataToNft,
} from "../../../solana/methods/general";
import cardReceive from "../../../assets/cardReceive.svg";
import cardSend from "../../../assets/cardSend.svg";
import { ReactComponent as solseaLogo } from "../../../assets/solseaLogo.svg";
import { ReactComponent as magicEdenLogo } from "../../../assets/magicEdenLogo.svg";
import { ReactComponent as unqLogo } from "../../../assets/unqSvgLogo.svg";
import { LAMPORTS_PER_SOL, PublicKey, SystemProgram } from "@solana/web3.js";
import BuyNftProposal from "./BuyNftProposal/BuyNftProposal";
import formConfig from "../formConfig";
import SellNftProposal from "./SellNftProposal/SellNftProposal";
import {
  CREATE_BUY_PROPOSAL_FEE,
  CREATE_DISCUSSION_PROPOSAL_FEE,
  CREATE_SELL_PROPOSAL_FEE,
  CREATE_TRANSFER_PROPOSAL_FEE,
  CREATE_UPDATE_GOVERNANCE_CONFIG_FEE,
  CREATE_WITHDRAWAL_PROPOSAL_FEE,
} from "../../../common/constants/fees";
import WithdrawalProposal from "./WithdrawalProposal/WithdrawalProposal";
import { createNotification } from "../../../utilities/notification";
import {
  EMPTY_STRING,
  MESSAGE_TYPE,
  NATIVE_MINT_DECIMAL_PLACES,
} from "../../../common/constants/common.constants";

import { calculateCharacterBytes } from "../../../solana/common/helpers/common.helpers";
import { getNftDataFromMagicEden } from "../../../api/magic-eden.api";

import { PROPOSAL_DESCRIPTION_MAX_LENGTH } from "../../../common/constants/club.constants";
import {
  getMintFromMagicEdenUrl,
  validateMintFromUrl,
} from "../../../solana/common/helpers/magic-eden.helpers";
import MintSelect from "./TransferProposal/MintSelect/MintSelect";
import {
  getMaxDecimalsLengthPerMint,
  getMintDecimalsDivider,
} from "../../../utilities/helpers";
import { NATIVE_MINT } from "@solana/spl-token";
import { ClubType } from "../../../common/enums/club.enum";
import SellOption from "./SellNftProposal/SellOption/SellOption";
import { getNftDataFromSolsea } from "../../../api/solsea.api";
import { getMintFromSolseaUrl } from "../../../solana/common/helpers/solsea.helpers";
import ToggleButon from "../../ToggleButton/ToggleButton";
import UpdateConfigProposal from "./UpdateConfigProposal/UpdateConfigProposal";
import { clubsStore } from "../../../contexts/clubStore";
import InfoBanner from "../../InfoBanner/InfoBanner";
import {
  getMaxVotingTime,
  getTradeAndTransferApprovalQuorum,
} from "../../../solana/common/helpers/proposals.helpers";

const CreateProposal: FC<ICreateProposalProps> = ({
  formFields,
  proposalToBuy,
  nftToSell,
}) => {
  const { setFieldValue, values, setFieldError } =
    useFormikContext<ICreateProposalForm>();

  const [proposalOption, setProposalOption] = useState(
    nftToSell ? ProposalAction.SellNft : ProposalAction.BuyNft
  );
  const [nftData, setNftData] = useState<IDetailNftData>();
  const { treasury, clubBasicInfo, vault } = clubsStore();

  const [descriptionLength, setDescriptionLength] = useState(0);

  useEffect(() => {
    // todo: ask should I remove the commented logic?
    // void getMaxVotingTime();
    updateFormikFields();
  }, []);

  useEffect(() => {
    if (values.nftToBuy !== EMPTY_STRING) void getNftData();
    else {
      setFieldValue(formFields.price.name, EMPTY_STRING);
    }
    if (clubBasicInfo.type !== ClubType.RoleBased) {
      setFieldValue(
        formFields.proposalOption.name,
        ProposalAction.UpdateGovernanceConfig
      );
    }
  }, [values.nftToBuy]);

  useEffect(() => {
    setFieldValue(formFields.price.name, EMPTY_STRING);
    setFieldError(formFields.price.name, EMPTY_STRING);
    setFieldValue(formFields.nft.name, undefined);
    setNftData(undefined);
    setFieldError(formFields.nftToBuy.name, EMPTY_STRING);
    setFieldValue(formFields.nftToBuy.name, EMPTY_STRING);
    setupMintValue();
  }, [values.buyOption, values.proposalOption, values.sellOption, treasury]);

  const proposalType =
    values.proposalType !== ProposalTypeNew.TreasuryVault &&
    !proposalToBuy &&
    !nftToSell
      ? values.proposalType
      : ProposalTypeNew.TreasuryVault;

  const setupMintValue = () => {
    if (values.proposalOption === ProposalAction.BuyNft) {
      if (values.buyOption === ProposalBuyOption.PeerToPeer) {
        setFieldValue(
          "mint",
          treasury?.denominatedTokenInfo?.mint ?? NATIVE_MINT
        );
      } else {
        setFieldValue("mint", NATIVE_MINT);
      }
    }
    if (values.proposalOption === ProposalAction.SellNft) {
      if (values.sellOption === ProposalBuyOption.PeerToPeer) {
        setFieldValue(
          "mint",
          treasury?.denominatedTokenInfo?.mint ?? NATIVE_MINT
        );
      } else {
        setFieldValue("mint", NATIVE_MINT);
      }
    }
    if (
      proposalType === ProposalTypeNew.Transfer ||
      proposalType === ProposalTypeNew.Withdrawal
    ) {
      setFieldValue(
        "mint",
        treasury?.denominatedTokenInfo?.mint ?? NATIVE_MINT
      );
    }
  };

  const maxVotingTimeBanner = useMemo(() => {
    if (treasury) {
      const price =
        proposalType === ProposalTypeNew.Transfer
          ? values.amountToInvest
          : values.price;
      const denomMint =
        treasury?.denominatedTokenInfo?.mint.toString() ??
        NATIVE_MINT.toString();
      const treasuryToken = treasury!.tokenAccounts.find(
        (item) => item.mint.toString() === values.mint.toString()
      );
      const approvalVotePercentage = getTradeAndTransferApprovalQuorum(
        treasury,
        price === EMPTY_STRING,
        Number(price) *
          getMintDecimalsDivider(
            treasuryToken?.decimals ?? NATIVE_MINT_DECIMAL_PLACES
          ),
        denomMint.toString() === values.mint.toString(),
        proposalType,
        clubBasicInfo.governances
      ).toString();

      const maxVotingTime = getMaxVotingTime(
        treasury,
        clubBasicInfo.governances,
        proposalType
      );

      return [
        {
          label: "Approval vote percentage",
          value: approvalVotePercentage,
        },
        { label: "Max voting time", value: `${maxVotingTime} days` },
      ];
    }
  }, [
    treasury,
    clubBasicInfo,
    values.price,
    values.mint,
    values.amountToInvest,
    proposalType,
    values.governanceType,
  ]);

  const updateFormikFields = () => {
    if (proposalToBuy) {
      setFieldValue(
        formFields.proposalType.name,
        ProposalTypeNew.TreasuryVault
      );
    }
    if (nftToSell) {
      setFieldValue(
        formFields.proposalType.name,
        ProposalTypeNew.TreasuryVault
      );
      setFieldValue(formFields.proposalOption.name, ProposalAction.SellNft);
    }
  };

  const validateNftMint = async (nftMint: string) => {
    try {
      const mint = new PublicKey(nftMint);
      const metadata = await getMintMetadata(mint);
      return parseMetadataToNft(metadata, mint);
    } catch (error) {
      return undefined;
    }
  };

  const getMagicEdenApiData = useCallback(async () => {
    const nftMint = getMintFromMagicEdenUrl(values.nftToBuy);
    if (!validateMintFromUrl(nftMint)) {
      setFieldValue(formFields.isNftMintValid.name, false);
      setFieldValue(formFields.price.name, EMPTY_STRING);
      return;
    }

    const magicEdenNftData = await getNftDataFromMagicEden(nftMint);
    setFieldValue(formFields.isNftMintValid.name, true);
    setFieldValue(formFields.magicEdenNftData.name, magicEdenNftData);

    if (magicEdenNftData.listing[0]) {
      setFieldValue(formFields.price.name, magicEdenNftData.listing[0].price);
    } else {
      createNotification(MESSAGE_TYPE.ERROR, "Nft not listed on Magic Eden");
    }

    return {
      mint: new PublicKey(nftMint),
      name: magicEdenNftData.nft.name,
      nftUrl: magicEdenNftData.nft.image,
      description: magicEdenNftData.nft.description,
      collection: {
        name: magicEdenNftData.nft.collection,
        family: EMPTY_STRING,
      },
    };
  }, [values.nftToBuy]);

  const getSolseaApiData = useCallback(async () => {
    try {
      const nftMint = getMintFromSolseaUrl(values.nftToBuy);
      if (!validateMintFromUrl(nftMint)) {
        setFieldValue(formFields.isNftMintValid.name, false);
        setFieldValue(formFields.price.name, EMPTY_STRING);
        return;
      }
      const solseaNftData = await getNftDataFromSolsea(nftMint);
      if (solseaNftData.currency !== SystemProgram.programId.toString()) {
        throw new Error("Can not buy on SolSea if currency is not SOL!");
      }

      if (solseaNftData.sellerKey === vault?.vaultAddress?.toString()) {
        throw new Error("Can not buy NFT listed by your club!");
      }
      setFieldValue(
        formFields.sellerFeeBps.name,
        solseaNftData.seller_fee_basis_points / 100
      );
      setFieldValue(
        formFields.price.name,
        solseaNftData.price / LAMPORTS_PER_SOL
      );
      setFieldValue(formFields.isNftMintValid.name, true);
      return {
        mint: new PublicKey(nftMint),
        name: solseaNftData.Title,
        nftUrl: solseaNftData.Preview_URL,
        description: solseaNftData.description,
        collection: {
          name: solseaNftData.nft_collection?.title,
          family: EMPTY_STRING,
        },
      };
    } catch (error: any) {
      setFieldValue(formFields.isNftMintValid.name, false);
      setFieldValue(formFields.price.name, EMPTY_STRING);
      createNotification(
        MESSAGE_TYPE.ERROR,
        "SolSea link not valid",
        error.message
      );
    }
  }, [values.nftToBuy]);

  const getNftData = async () => {
    try {
      switch (values.buyOption) {
        case ProposalBuyOption.PeerToPeer: {
          const nftData = await validateNftMint(values.nftToBuy);
          if (!nftData) setFieldValue(formFields.isNftMintValid.name, false);
          else {
            setNftData(nftData);
            setFieldValue(formFields.isNftMintValid.name, true);
          }
          break;
        }
        case ProposalBuyOption.MagicEden: {
          setNftData(await getMagicEdenApiData());
          break;
        }
        case ProposalBuyOption.Solsea:
          setNftData(await getSolseaApiData());
          break;
        default:
          setFieldValue(formFields.isNftMintValid.name, false);
      }
    } catch (error) {
      setFieldValue(formFields.isNftMintValid.name, false);
      createNotification(MESSAGE_TYPE.ERROR, "Failed to get NFT listing data!");
    }
  };

  const calculateBuyteLength = useCallback((e) => {
    const byteAmount = calculateCharacterBytes(e.target.value);
    if (byteAmount <= PROPOSAL_DESCRIPTION_MAX_LENGTH) {
      setFieldValue(
        formConfig.formFields.proposalDescription.name,
        e.target.value
      );

      setDescriptionLength(byteAmount);
    }
  }, []);

  const showExternalLink = useMemo(() => {
    return (
      proposalType === ProposalTypeNew.Discussion ||
      proposalType === ProposalTypeNew.Withdrawal ||
      proposalType === ProposalTypeNew.Transfer ||
      (proposalType === ProposalTypeNew.TreasuryVault &&
        proposalOption === ProposalAction.BuyNft) ||
      proposalType === ProposalTypeNew.UpdateClubConfig
    );
  }, [proposalType]);

  const proposalFee = useMemo(() => {
    switch (proposalType) {
      case ProposalTypeNew.Discussion:
        return CREATE_DISCUSSION_PROPOSAL_FEE;
      case ProposalTypeNew.TreasuryVault:
        return proposalOption === ProposalAction.BuyNft
          ? CREATE_BUY_PROPOSAL_FEE
          : CREATE_SELL_PROPOSAL_FEE;
      case ProposalTypeNew.Withdrawal:
        return CREATE_WITHDRAWAL_PROPOSAL_FEE;
      case ProposalTypeNew.Transfer:
        return CREATE_TRANSFER_PROPOSAL_FEE;
      case ProposalTypeNew.UpdateClubConfig:
      case ProposalTypeNew.AddSellPermission:
        return CREATE_UPDATE_GOVERNANCE_CONFIG_FEE;
    }
  }, [proposalOption]);

  const warningDescription = useMemo(() => {
    const mainMint = treasury?.denominatedTokenInfo?.mint ?? NATIVE_MINT;
    return (
      proposalType === ProposalTypeNew.TreasuryVault &&
      values.mint.toString() !== mainMint?.toString() &&
      "Since the chosen currency is not Main Currency, the General Voting configuration will be used."
    );
  }, [
    values.mint,
    proposalType,
    treasury,
    values.buyOption,
    values.sellOption,
    proposalOption,
  ]);

  return (
    <div className="create-proposal">
      <div className="create-proposal__content">
        <WizardField
          name={formFields.proposalTitle.name}
          label={formFields.proposalTitle.label}
          type="text"
          placeholder="Type proposal title here"
          required
        />
        <div className="create-proposal__description">
          <WizardField
            type="textarea"
            placeholder="Type proposal description here"
            name={formFields.proposalDescription.name}
            onChange={calculateBuyteLength}
            label={formFields.proposalDescription.label}
            required
          />
          <p className="create-proposal__char-count">
            {values.proposalDescription.length}/
            {PROPOSAL_DESCRIPTION_MAX_LENGTH}
          </p>
        </div>

        {/* TODO @goran:Make this a generic component(same is used in club basic info) */}
        {proposalType === ProposalTypeNew.TreasuryVault && (
          <>
            <div className="create-proposal__option">
              <p className="create-proposal__option-title">Choose option</p>
              <div className="create-proposal__options">
                <div
                  onClick={() => {
                    setProposalOption(ProposalAction.BuyNft);
                    setFieldValue("proposalOption", ProposalAction.BuyNft);
                    setFieldValue("isMagicEdenNft", false);
                  }}
                  className={`create-proposal__button ${
                    proposalOption === ProposalAction.BuyNft &&
                    `create-proposal__button--active`
                  }`}
                >
                  <img src={walletReceive} alt="wallet_deposit" />
                  <p className="create-proposal__option-name">NFT to buy</p>
                </div>
                <div
                  onClick={() => {
                    setProposalOption(ProposalAction.SellNft);
                    setFieldValue("proposalOption", ProposalAction.SellNft);
                    setFieldValue("buyOption", ProposalBuyOption.PeerToPeer);
                  }}
                  className={`create-proposal__button ${
                    proposalOption === ProposalAction.SellNft &&
                    `create-proposal__button--active`
                  }`}
                >
                  <img src={walletSend} alt="wallet_withraw" />
                  <p className="create-proposal__option-name">NFT to sell</p>
                </div>
              </div>
            </div>
            {proposalOption === ProposalAction.BuyNft && (
              <div className="create-proposal__buy-option">
                {/* TODO@goran:comment in this when we decide to implement this feature */}
                {/* <SellOption
                  title="UNQ Marketplace"
                  handleClick={() =>
                    setFieldValue(
                      formConfig.formFields.buyOption.name,
                      ProposalBuyOption.UnqMarketplace
                    )
                  }
                  isActive={
                    values.buyOption === ProposalBuyOption.UnqMarketplace
                  }
                /> */}
                <SellOption
                  title="Magic Eden"
                  handleClick={() =>
                    setFieldValue(
                      formConfig.formFields.buyOption.name,
                      ProposalBuyOption.MagicEden
                    )
                  }
                  imageUrl={{ logo: magicEdenLogo }}
                  isActive={values.buyOption === ProposalBuyOption.MagicEden}
                />
                <SellOption
                  title="SolSea"
                  handleClick={() =>
                    setFieldValue(
                      formConfig.formFields.buyOption.name,
                      ProposalBuyOption.Solsea
                    )
                  }
                  imageUrl={{ logo: solseaLogo }}
                  isActive={values.buyOption === ProposalBuyOption.Solsea}
                />
                <SellOption
                  title="Peer to peer"
                  handleClick={() =>
                    setFieldValue(
                      formConfig.formFields.buyOption.name,
                      ProposalBuyOption.PeerToPeer
                    )
                  }
                  imageUrl={{ logo: unqLogo }}
                  isActive={values.buyOption === ProposalBuyOption.PeerToPeer}
                />
              </div>
            )}
            {proposalOption === ProposalAction.BuyNft && (
              <BuyNftProposal
                form={formConfig}
                nftData={nftData!}
                validateNftMint={() => validateNftMint(values.nftToBuy)}
              />
            )}
            {proposalOption === ProposalAction.SellNft && (
              <SellNftProposal form={formConfig} nftToSell={nftToSell} />
            )}
          </>
        )}
        {proposalType === ProposalTypeNew.Withdrawal && (
          <WithdrawalProposal formFields={formConfig.formFields} />
        )}

        {(proposalType === ProposalTypeNew.UpdateClubConfig ||
          proposalType === ProposalTypeNew.AddSellPermission) && (
          <>
            <ToggleButon
              title="Choose option"
              items={[
                {
                  description: EMPTY_STRING,
                  icon: blackCart,
                  isActive:
                    values.proposalOption === ProposalAction.UpdateRoleConfig,
                  onClick: () =>
                    setFieldValue(
                      formConfig.formFields.proposalOption.name,
                      ProposalAction.UpdateRoleConfig
                    ),
                  title: "Change role voting power",
                  disabled: clubBasicInfo.type !== ClubType.RoleBased,
                },
                {
                  description: EMPTY_STRING,
                  icon: cardSend,
                  isActive:
                    values.proposalOption ===
                    ProposalAction.UpdateGovernanceConfig,
                  onClick: () =>
                    setFieldValue(
                      formConfig.formFields.proposalOption.name,
                      ProposalAction.UpdateGovernanceConfig
                    ),
                  title: "Change approval vote percentage",
                },
              ]}
            />
            <UpdateConfigProposal formConfig={formConfig} />
          </>
        )}

        {warningDescription && (
          <div className="bulk-modal__warning">
            <p>{warningDescription}</p>
          </div>
        )}
        {proposalType === ProposalTypeNew.Transfer && treasury && (
          <div>
            <div className="create-proposal__mint-and-amount">
              <WizardField
                type="number"
                placeholder="Enter the amount here"
                name={formFields.amountToInvest.name}
                label={formFields.amountToInvest.label}
                maxDecimalsLength={getMaxDecimalsLengthPerMint(
                  treasury.denominatedTokenInfo?.decimals
                )}
                required
              />
              <MintSelect
                treasuryTokenAccounts={treasury.tokenAccounts}
                isForm
                setFieldValue={setFieldValue}
                chosenMint={treasury.denominatedTokenInfo?.mint.toString()}
                isDisabled={clubBasicInfo.type === ClubType.ProjectFundraise}
              />
            </div>
            <WizardField
              type="text"
              placeholder="Enter the address here"
              name={formFields.transferAddress.name}
              label={formFields.transferAddress.label}
              required
            />
          </div>
        )}
        {proposalType !== ProposalTypeNew.Withdrawal && maxVotingTimeBanner && (
          <InfoBanner labelValue={maxVotingTimeBanner}></InfoBanner>
        )}
        {showExternalLink && (
          <div>
            <WizardField
              type="text"
              placeholder="Enter external discussion URL here"
              name={formFields.link.name}
              label={formFields.link.label}
            />
            <p className="create-proposal__discussion-description">
              Here you can add a link to discuss the proposal on other platforms
              such as Discord, Forum etc.
            </p>
          </div>
        )}
        {proposalType !== ProposalTypeNew.Withdrawal && (
          <div>
            {/* <p className="create-proposal__max-voting-time-label">
              Max voting time
            </p>
            {maxVotingTime && (
              <p className="create-proposal__max-voting-time-value">
                {maxVotingTime}
              </p>
            )} */}
          </div>
        )}
      </div>
      <Fees
        feesDescription={`${proposalFee} SOL`}
        feesTitle="Creating proposal fee"
      />
    </div>
  );
};

export default CreateProposal;
